import { useEffect, useState } from "react";

/**
 * Набор параметров расчёта (ТЗ 3.3, экран «Настройки»; ТЗ 4.1: каждый порог и окно — параметр с меткой источника).
 * Хранится в браузере пользователя; сервер считает с этими параметрами, а сигналы в БД пишутся с набором по умолчанию.
 */
export interface ParamSet {
  cotShort: number;
  cotLong: number;
  willco: number;
  oi: number;
  sma: number;
  smaB: number;
  extreme: number;
  progo: number;
  progoDiv: number;
  willcoMode: "NET" | "SHORT";
  adjust: "DIFF" | "NONE";
  priceOverMa: boolean;
  flatMaAllowsBuy: boolean;
  oiInM19ForIndex: boolean;
  channel: number;
  maCross: number;
  csHigh: number;
  csLow: number;
  csStrong: number;
  seasonTol: number;
  jilerMode: "PP" | "REL";
  jilerYears: number;
  maxLoss: number;
  // аудит 5
  pubLag: number;
  costPct: number;
  seasonMinYears: number;
  jilerMinYears: number;
  jilerResearch: boolean;
  cotHigh: number;
  cotLow: number;
  willcoHigh: number;
  willcoLow: number;
  willcoStrong: number;
  oiLow: number;
  oiHigh: number;
}

export type Source = "книга" | "по умолчанию" | "допущение";

export interface ParamMeta {
  key: keyof ParamSet;
  label: string;
  source: Source;
  module: string;
  kind: "int" | "num" | "bool" | "enum";
  options?: [string, string][];
  min?: number;
  max?: number;
  hint?: string;
}

export const DEFAULTS: ParamSet = {
  cotShort: 26,
  cotLong: 156,
  willco: 26,
  oi: 52,
  sma: 52,
  smaB: 39,
  extreme: 208,
  progo: 14,
  progoDiv: 20,
  willcoMode: "NET",
  adjust: "DIFF",
  priceOverMa: false,
  flatMaAllowsBuy: true,
  oiInM19ForIndex: false,
  channel: 26,
  maCross: 1,
  csHigh: 90,
  csLow: 10,
  csStrong: 97,
  seasonTol: 2,
  jilerMode: "PP",
  jilerYears: 10,
  maxLoss: 20000,
  pubLag: 2,
  costPct: 0.1,
  seasonMinYears: 5,
  jilerMinYears: 6,
  jilerResearch: false,
  cotHigh: 80,
  cotLow: 20,
  willcoHigh: 75,
  willcoLow: 25,
  willcoStrong: 90,
  oiLow: 20,
  oiHigh: 80,
};

export const META: ParamMeta[] = [
  { key: "cotShort", label: "COT Index, короткое окно, недель", source: "книга", module: "M3", kind: "int", min: 4, max: 520, hint: "Гл. 10, 13: «six-month COT index»" },
  { key: "cotLong", label: "COT Index «три года», недель", source: "книга", module: "M3", kind: "int", min: 4, max: 1040, hint: "Гл. 4: три года = 156 недель (ТЗ M3 v1). Другое окно помечается как исследовательское. В сетап сделок канона не входит" },
  { key: "cotHigh", label: "COT Index: бычья зона ≥", source: "книга", module: "M3", kind: "num", min: 50, max: 100, hint: "Гл. 4: 80" },
  { key: "cotLow", label: "COT Index: медвежья зона ≤", source: "книга", module: "M3", kind: "num", min: 0, max: 50, hint: "Гл. 4: 20" },
  { key: "willco", label: "WILLCO, недель", source: "книга", module: "M12", kind: "int", min: 4, max: 520, hint: "Гл. 10: 26 недель «лучше во всех отношениях»" },
  { key: "willcoHigh", label: "WILLCO: зона покупки ≥", source: "книга", module: "M12", kind: "num", min: 50, max: 100, hint: "Гл. 10: «buy trigger zone of 75–80»" },
  { key: "willcoLow", label: "WILLCO: зона продажи ≤", source: "по умолчанию", module: "M12", kind: "num", min: 0, max: 50, hint: "Зеркало 75; в книге числа нет" },
  { key: "willcoStrong", label: "WILLCO: сильный сетап ≥", source: "книга", module: "M12", kind: "num", min: 50, max: 100, hint: "Гл. 10: золото, май 2004 — выше 90" },
  { key: "willcoMode", label: "WILLCO: формула", source: "книга", module: "M12", kind: "enum", options: [["NET", "Нетто / OI (Genesis)"], ["SHORT", "100 − шорт / OI (графики гл. 10)"]] },
  { key: "oi", label: "Индекс открытого интереса, недель", source: "книга", module: "M8", kind: "int", min: 4, max: 520, hint: "Гл. 8: «12-month stochastic of OI»" },
  { key: "oiLow", label: "Индекс OI: «в бассейне никого» ≤", source: "по умолчанию", module: "M8", kind: "num", min: 0, max: 50 },
  { key: "oiHigh", label: "Индекс OI: толпа в рынке ≥", source: "по умолчанию", module: "M8", kind: "num", min: 50, max: 100 },
  { key: "sma", label: "Тренд A: SMA, недель", source: "книга", module: "M17", kind: "int", min: 2, max: 260, hint: "Гл. 13" },
  { key: "smaB", label: "Тренд B: SMA, недель", source: "книга", module: "M17", kind: "int", min: 2, max: 260, hint: "Гл. 15" },
  { key: "flatMaAllowsBuy", label: "Тренд B: «плоская SMA и цены снижаются — можно покупать»", source: "книга", module: "M17", kind: "bool", hint: "Дословное правило гл. 15 (ТЗ flat_ma_allows_buy)" },
  { key: "priceOverMa", label: "Тренд B: закрытие выше/ниже SMA главнее наклона", source: "по умолчанию", module: "M17", kind: "bool", hint: "Неоднозначность гл. 15" },
  { key: "maCross", label: "Пересечение SMA: порог θ, %", source: "по умолчанию", module: "M17", kind: "num", min: 0, max: 20 },
  { key: "extreme", label: "Многолетний экстремум нетто, недель", source: "книга", module: "M2", kind: "int", min: 26, max: 1040, hint: "Гл. 3: максимум или минимум за 4 года" },
  { key: "progo", label: "ProGo: среднее, дней", source: "книга", module: "M13", kind: "int", min: 1, max: 100 },
  { key: "progoDiv", label: "ProGo: окно дивергенций, дней", source: "по умолчанию", module: "M13", kind: "int", min: 2, max: 200 },
  { key: "channel", label: "Канал пробоя, недель", source: "книга", module: "M6-A", kind: "int", min: 2, max: 104, hint: "Гл. 6: 26 недель" },
  { key: "csHigh", label: "Доля шорта в OI: «высокая» — перцентиль", source: "по умолчанию", module: "M10", kind: "num", min: 50, max: 100, hint: "Пороги 55/65 откалиброваны автором на иене" },
  { key: "csLow", label: "Доля шорта в OI: «низкая» — перцентиль", source: "по умолчанию", module: "M10", kind: "num", min: 0, max: 50 },
  { key: "csStrong", label: "Доля шорта в OI: «крайне высокая» (с сезонной вершиной — сила 2) — перцентиль", source: "по умолчанию", module: "M10", kind: "num", min: 50, max: 100, hint: "У Уильямса 65 % на иене (ТЗ: cs_oi_signal = −2); не ниже «высокой»" },
  { key: "seasonTol", label: "Сезонное окно: ± недель от вершины / дна", source: "по умолчанию", module: "M11", kind: "int", min: 0, max: 10 },
  { key: "seasonMinYears", label: "Сезонность: минимум лет истории для сигналов", source: "по умолчанию", module: "M11", kind: "int", min: 1, max: 30, hint: "В книге — 27 лет для иены, минимум не задан. Короче — кривая показывается как ненадёжная и в сигналы не идёт" },
  { key: "jilerMode", label: "Метод Джайлера: отклонение", source: "по умолчанию", module: "M0", kind: "enum", options: [["PP", "в процентных пунктах"], ["REL", "относительное, %"]], hint: "Неоднозначность гл. 1" },
  { key: "jilerYears", label: "Метод Джайлера: лет нормы", source: "по умолчанию", module: "M0", kind: "int", min: 1, max: 20 },
  { key: "jilerMinYears", label: "Метод Джайлера: минимум прошлых лет", source: "книга", module: "M0", kind: "int", min: 1, max: 20, hint: "В примерах книги — 6 и 10 лет" },
  { key: "jilerResearch", label: "Метод Джайлера: исследовательская оценка на короткой истории (от 2 лет)", source: "по умолчанию", module: "M0", kind: "bool", hint: "Сигналы помечаются «[исследование]» — это не метод Джайлера" },
  { key: "pubLag", label: "Позиции дня D используются с открытия дня D + N", source: "допущение", module: "1.4", kind: "int", min: 0, max: 10, hint: "ISS публикует день D на D+1, время неизвестно: 2 — консервативно, 1 — если публикация до открытия, 0 — заглядывание вперёд" },
  { key: "costPct", label: "Издержки на сделку туда-обратно, % цены", source: "допущение", module: "Бэктест", kind: "num", min: 0, max: 5, hint: "Комиссия + проскальзывание; точный тариф не задан. Чувствительность — на странице «Бэктест»" },
  { key: "maxLoss", label: "Допустимый убыток на контракт, руб.", source: "по умолчанию", module: "M16", kind: "num", min: 100, max: 10_000_000, hint: "Гл. 12: стоп от денег, а не от фигур графика" },
  { key: "oiInM19ForIndex", label: "Индекс OI фьючерсов на индексы — в полном сетапе", source: "по умолчанию", module: "M19", kind: "bool", hint: "ТЗ 4.1: только при явном включении" },
  { key: "adjust", label: "Склейка контрактов", source: "по умолчанию", module: "1.4", kind: "enum", options: [["DIFF", "Разностная"], ["NONE", "Без корректировки"]] },
];

/**
 * Правила сделок и сигналов — только канон Уильямса (LW_CANONICAL, решение 21.10.2026): выбора профиля нет. Подписи
 * на сайте берут параметры канона отсюда (зеркало EntryRules.LW_CANONICAL на сервере).
 */
export const CANON = { title: "канон Уильямса", zone: 20, trailDays: 17 } as const;

const KEY = "insidera-params";
const EVENT = "insidera:params";

export function loadParams(): ParamSet {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    // Выбор профиля правил убран: сохранённое раньше значение entryProfile игнорируется.
    const { entryProfile: _removed, ...saved } = JSON.parse(raw) as Partial<ParamSet> & { entryProfile?: string };
    void _removed;
    return { ...DEFAULTS, ...saved };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveParams(p: ParamSet) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* хранилище недоступно — набор действует до перезагрузки */
  }
  current = p;
  window.dispatchEvent(new Event(EVENT));
}

let current: ParamSet | null = null;

export function useParams(): ParamSet {
  const [p, setP] = useState<ParamSet>(() => current ?? (current = loadParams()));
  useEffect(() => {
    const on = () => setP(current ?? loadParams());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return p;
}

export function isDefault(p: ParamSet) {
  return (Object.keys(DEFAULTS) as (keyof ParamSet)[]).every((k) => p[k] === DEFAULTS[k]);
}

/*
 * Панели графика (ТЗ 3.1: «панели включаются и переставляются пользователем; набор по умолчанию: P0, P1, P2 (N = 26),
 * P6, P8»). Хранится в браузере; null — набор по умолчанию.
 */
export interface PaneLayout {
  order: string[];
  shown: string[];
}

/** P0 цена (+ полоса тренда под ней), P1 нетто, P2 COT Index, P6 индекс OI, P8 WILLCO. */
export const DEFAULT_SHOWN = ["price", "trendbar", "net", "cot", "oi", "willco"];

const PANES_KEY = "insidera-panes";
const PANES_EVENT = "insidera:panes";

function loadPaneLayout(): PaneLayout | null {
  try {
    const raw = localStorage.getItem(PANES_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as PaneLayout;
    return Array.isArray(v.order) && Array.isArray(v.shown) ? v : null;
  } catch {
    return null;
  }
}

let paneLayout: PaneLayout | null | undefined;

export function savePaneLayout(l: PaneLayout | null) {
  try {
    if (l) localStorage.setItem(PANES_KEY, JSON.stringify(l));
    else localStorage.removeItem(PANES_KEY);
  } catch {
    /* хранилище недоступно — раскладка действует до перезагрузки */
  }
  paneLayout = l;
  window.dispatchEvent(new Event(PANES_EVENT));
}

export function usePaneLayout(): PaneLayout | null {
  const [l, setL] = useState<PaneLayout | null>(() => (paneLayout !== undefined ? paneLayout : (paneLayout = loadPaneLayout())));
  useEffect(() => {
    const on = () => setL(paneLayout ?? null);
    window.addEventListener(PANES_EVENT, on);
    return () => window.removeEventListener(PANES_EVENT, on);
  }, []);
  return l;
}

/** Панели в порядке раскладки (новые, которых нет в сохранённом порядке, — в конце) и видимость каждой. */
export function arrangePanes<T extends { id: string }>(panes: T[], l: PaneLayout | null): { pane: T; shown: boolean }[] {
  const pos = new Map((l?.order ?? []).map((id, i) => [id, i]));
  const sorted = [...panes].sort((a, b) => (pos.get(a.id) ?? 1e6 + panes.indexOf(a)) - (pos.get(b.id) ?? 1e6 + panes.indexOf(b)));
  const shown = new Set(l?.shown ?? DEFAULT_SHOWN);
  return sorted.map((pane) => ({ pane, shown: shown.has(pane.id) }));
}

/*
 * «Показывать отфильтрованные сигналы» — одна настройка для графика, списка сигналов рынка и журнала всех рынков.
 * Только отображение: данные, статистика и бэктест от неё не зависят. По умолчанию выключена (как было на графике).
 */
const SHOW_FILTERED_KEY = "insidera.showMuted";
const SHOW_FILTERED_EVENT = "insidera:showMuted";

function loadShowFiltered(): boolean {
  try {
    return localStorage.getItem(SHOW_FILTERED_KEY) === "1";
  } catch {
    return false;
  }
}

let showFiltered: boolean | undefined;

export function useShowFiltered(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => (showFiltered !== undefined ? showFiltered : (showFiltered = loadShowFiltered())));
  useEffect(() => {
    const on = () => setV(showFiltered ?? false);
    window.addEventListener(SHOW_FILTERED_EVENT, on);
    return () => window.removeEventListener(SHOW_FILTERED_EVENT, on);
  }, []);
  const set = (next: boolean) => {
    showFiltered = next;
    try {
      localStorage.setItem(SHOW_FILTERED_KEY, next ? "1" : "0");
    } catch {
      /* без сохранения — до перезагрузки */
    }
    window.dispatchEvent(new Event(SHOW_FILTERED_EVENT));
  };
  return [v, set];
}
